import { useState, useCallback, useRef, useEffect } from 'react';
import { Search, Loader2, ExternalLink } from 'lucide-react';
import { searchPhotos, trackDownload, type UnsplashPhoto } from '../../services/unsplashService';

interface UnsplashPickerProps {
  onSelect: (photo: {
    url: string;
    alt: string;
    unsplashId: string;
    unsplashAuthor: string;
    unsplashAuthorUrl: string;
  }) => void;
}

export function UnsplashPicker({ onSelect }: UnsplashPickerProps) {
  const [query, setQuery] = useState('');
  const [photos, setPhotos] = useState<UnsplashPhoto[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const doSearch = useCallback(async (q: string, p: number) => {
    if (!q.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await searchPhotos(q.trim(), p);
      if (p === 1) {
        setPhotos(res.results);
      } else {
        setPhotos((prev) => [...prev, ...res.results]);
      }
      setTotalPages(res.total_pages);
      setPage(p);
    } catch (err: any) {
      setError(err.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (value: string) => {
    setQuery(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (value.trim()) {
        setPhotos([]);
        doSearch(value, 1);
      }
    }, 400);
  };

  const handleSelect = async (photo: UnsplashPhoto) => {
    trackDownload(photo.links.download_location);
    onSelect({
      url: photo.urls.regular,
      alt: photo.alt_description || 'Unsplash photo',
      unsplashId: photo.id,
      unsplashAuthor: photo.user.name,
      unsplashAuthorUrl: photo.user.links.html,
    });
  };

  return (
    <div className="space-y-3">
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder="Search Unsplash photos..."
          className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              clearTimeout(debounceRef.current);
              setPhotos([]);
              doSearch(query, 1);
            }
          }}
        />
      </div>

      {/* Error */}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* Results grid */}
      {photos.length > 0 && (
        <div className="max-h-72 overflow-y-auto">
          <div className="grid grid-cols-3 gap-2">
            {photos.map((photo) => (
              <button
                key={photo.id}
                onClick={() => handleSelect(photo)}
                className="group relative aspect-square overflow-hidden rounded-lg border border-gray-200 hover:border-blue-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <img
                  src={photo.urls.small}
                  alt={photo.alt_description || ''}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-[10px] text-white truncate">{photo.user.name}</p>
                </div>
              </button>
            ))}
          </div>

          {/* Load more */}
          {page < totalPages && (
            <div className="flex justify-center pt-3">
              <button
                onClick={() => doSearch(query, page + 1)}
                disabled={loading}
                className="px-4 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Loading spinner */}
      {loading && photos.length === 0 && (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!loading && query && photos.length === 0 && !error && (
        <p className="text-sm text-gray-500 text-center py-4">No photos found</p>
      )}

      {/* Unsplash attribution */}
      <p className="text-[10px] text-gray-400 text-center">
        Photos by{' '}
        <a
          href="https://unsplash.com"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-gray-600 inline-flex items-center gap-0.5"
        >
          Unsplash <ExternalLink className="w-2.5 h-2.5" />
        </a>
      </p>
    </div>
  );
}
